import MojitoPresentation from 'mojito/presentation';
import { toJpeg } from 'html-to-image';

const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;

/**
 * A utility class for use within the ShareBet module.
 *
 * @name utils
 * @memberof Mojito.Modules.ShareDialog
 */

export default class ShareBetUtils {
    /**
     * Creates a deeplink URL for a bet by using an array of betRefs.
     * The function constructs the final URL including required query parameters such as
     * 'bet_ref', 'clear_slip', and 'source' by utilizing the given route resolver.
     *
     * @param {object} routeResolver - An object responsible for resolving routes dynamically.
     * @param {Array<string>} [betRefs=[]] - The list of betRefs.
     *
     * @returns {string} The fully constructed deeplink URL.
     *
     * @function Mojito.Modules.ShareDialog.utils.getDeeplink
     */
    static getDeeplink(routeResolver, betRefs = []) {
        // Construct the queryString
        const queryString = [
            ...betRefs.map(betRef => `bet_ref=${encodeURIComponent(betRef)}`),
            'clear_slip=Y',
            'source=SHARE_BET',
        ].join('&');

        // Get the base path using routeResolver
        const deepLinkPath = routeResolver.getRoute(NavigationPathBuilder.toBetslipDeepLink());

        return betRefs.length > 0
            ? `${location.origin}${deepLinkPath}?${queryString}`
            : `${location.origin}${deepLinkPath}`;
    }

    /**
     * Downloads an image based on base64-encoded data URL of the image.
     *
     * @param {string} dataUrl - The data URL for image.
     * @param {string} fileName - The file name for image.
     * @param {Function} onFinish - A callback function to be invoked when the download finishes.
     *
     * @function Mojito.Modules.ShareDialog.utils.downloadImage
     */
    static downloadImage(dataUrl, fileName, onFinish) {
        const link = document.createElement('a');
        link.download = fileName;
        link.href = dataUrl;
        link.click();
        onFinish();
    }

    /**
     * Converts a DOM node to a JPEG image and returns a data URL.
     *
     * @param {HTMLElement} node - The DOM node to convert to an image.
     * @param {object} options - The options for the conversion.
     * @returns {Promise<string>} A promise that resolves to the data URL of the image.
     *
     * @function Mojito.Modules.ShareDialog.utils.convertNodeToJpeg
     */
    static async convertNodeToJpeg(node, options) {
        return toJpeg(node, options);
    }

    /**
     * Waits for all images within a DOM node to be loaded or failed to load.
     * This function resolves when all images have completed loading or when the timeout is reached.
     *
     * @param {HTMLElement} clonedNode - The DOM node containing images to be loaded.
     * @param {number} [timeout=0] - Maximum time to wait for each image in milliseconds.
     * If 0, will wait indefinitely until the image loads or fails.
     * @returns {Promise<void>} A promise that resolves when all images have loaded or failed to load.
     *
     * @function Mojito.Modules.ShareDialog.utils.waitForImagesToLoad
     */
    static async waitForImagesToLoad(clonedNode, timeout = 0) {
        const images = clonedNode.querySelectorAll('img');
        const promises = Array.from(images).map(img => {
            if (img.complete) {
                return Promise.resolve();
            }

            return new Promise(resolve => {
                const timer = setTimeout(() => {
                    resolve();
                }, timeout);
                img.onload = () => {
                    clearTimeout(timer);
                    resolve();
                };
                img.onerror = () => {
                    clearTimeout(timer);
                    resolve();
                };
            });
        });

        await Promise.all(promises);
    }
}
